<template>
	<div class="col-span-full h-full flex flex-col pb-10 [scrollbar-gutter:stable]">

		<slot name="header">
			<!-- Default 'go back' button (through router history) -->
			<div class="flex items-center px-6 pt-6 flex-shrink-0">
				<button class="btn btn-ghost btn-sm capitalize" @click="$router.back()">
					<ArrowLeftIcon class="w-4 h-4"/>
					{{ $t('Return') }}
				</button>
			</div>
		</slot>

		<div v-if="isLoading || !authorData" class="flex items-center justify-center h-full">
			<span class="loading loading-spinner"></span>
		</div>
		<!-- Scrollable Content -->
		<div v-else class="flex-1 px-6">
			<!-- Publisher Profile Content -->
			<div class="grid grid-cols-3 px-8 pt-8" :class="inAgenda ? '' : 'md:grid-cols-4'">
				<div class="col-span-3">
					<div class="flex flex-col lg:grid lg:grid-cols-[2fr_3fr] gap-6 lg:gap-y-0">
						<!-- Left: Publisher image + name -->
						<div class="flex items-start gap-4">
							<UserAvatar :user="authorData.author" :usePublicProfile="authorData.section !== 'colleagues'" class="w-20 h-20" />
							<div class="flex flex-col gap-1">
								<p class="font-semibold text-base-content hyphens-auto">{{ authorData.section === 'colleagues'? authorData.author.name : authorData.author.publicName ?? authorData.author.name }}</p>
								<p class="text-base-content-light">{{ authorTypeLabel }}</p>
							</div>
						</div>

						<!-- Right: Publisher info -->
						<div v-if="authorData.author.publicDescription && authorData.section !== 'colleagues'" class="prose prose-gray max-w-prose">
							<span class="text-base-content-light mb-6 text-sm">
								<p class="whitespace-pre-line public-profile-description" v-html="urlsToHrefs(authorData.author.publicDescription)"></p>
							</span>
						</div>
					</div>

					<!-- Divider -->
					<hr class="border-gray-200 my-8" />

					<!-- Lesson Plan Lists Section or Detail View -->
					<div class="flex flex-col gap-2">
						<!-- Breadcrumb -->
						<div class="breadcrumbs max-w-full lg:max-w-3xl text-lg font-bold min-h-[3rem] overflow-hidden">
							<ul class="items-center min-w-0 flex h-8">
								<!-- Always visible root -->
								<li class="shrink-0">
									<a class="cursor-pointer" @click="selectedCollection = null">{{ $t('All collections') }}</a>
								</li>

								<!-- Second breadcrumb item (always mounted) -->
								<li v-if="selectedCollection" class="flex items-center min-w-0 mr-2">
									<!-- Breadcrumb title -->
									<p class="truncate max-w-md pr-2">
										{{ selectedCollection?.name || ' ' }}
									</p>
								</li>
							</ul>
						</div>


						<!-- Show collection content -->
						<div v-if="selectedCollection">
							<LessonPlanDiscoverAuthorCollection
								:collectionId="selectedCollection?.id"
								:authorId="authorData.author.id"
								@selectLessonPlan="$emit('selectLessonPlan', $event)"
								@createVariant="$emit('createVariant', $event)"
							/>
						</div>
						<!-- Show collection list -->
						<div v-else class="flex flex-col gap-4">
							<button
								v-for="collection in authorData.collections"
								:key="collection.id"
								@click="selectedCollection = collection"
								class="flex items-center gap-3 p-3 px-2 rounded-lg hover:bg-gray-100 transition-colors text-left"
							>
								<ListBulletIcon class="w-5 h-5 text-gray-600 flex-shrink-0" />
								<p class="font-semibold text-gray-900 truncate pr-6">{{ collection.name }}</p>
								<div class="ml-auto flex items-center gap-3">
									<div class="badge text-white min-w-6 border-0 bg-base-content text-xs" :class="collection.planCount > 0 ? '' : 'opacity-25'">{{ collection.planCount }}</div>
									<ChevronRightIcon class="w-4 h-4 text-base-300 stroke-2" />
								</div>
							</button>
						</div>

					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { mapStores } from 'pinia';
import { useLessonPlanCollectionsFromColleaguesStore, usePublicLessonPlanCollectionsStore, AuthorData } from '@/stores/DiscoverCollections.store';
import { useDiscoverStateStore } from '@/stores/DiscoverCollections.store';
import { LessonPlanCollection } from '@/models/LessonPlanCollection.model';

import { ChevronRightIcon, ArrowLeftIcon, ListBulletIcon } from '@heroicons/vue/24/outline';
import LessonPlanDiscoverAuthorCollection from '@/components/LessonPlanDiscoverAuthorCollection.vue';
import UserAvatar from '@/components/UserAvatar.vue';
import { isAgendaRoute } from "@/utils/RouteContext.util";


export default {
	name: 'LessonPlanDiscoverAuthor',

	components: {
		ChevronRightIcon,
		ArrowLeftIcon,
		ListBulletIcon,
		LessonPlanDiscoverAuthorCollection,
		UserAvatar,
	},

	emits: ['selectAuthor', 'createVariant', 'selectLessonPlan'],

	props: {
		authorId: {	// it's an id instead of some object because like this the value can be set directly from the route params
			type: String,
			required: true,
		},
		section: { // indicates whether the selected user/author should be rendered as a 'colleague' or 'author' (with all their public collections)
			type: String,
			default: 'public',
		},
	},

	data() {
		return {
			selectedCollection: null as LessonPlanCollection | null,
		};
	},

	computed: {
		// shown inside a drawer in the agenda, with less room
		inAgenda(): boolean {
			return isAgendaRoute(this.$router);
		},

		...mapStores(useDiscoverStateStore),

		collectionsFromColleaguesStore() {
			return useLessonPlanCollectionsFromColleaguesStore();
		},

		publicCollectionsStore() {
			return usePublicLessonPlanCollectionsStore();
		},

		isLoading() {
			return this.collectionsFromColleaguesStore.loading || this.publicCollectionsStore.loading;
		},

		authorTypeLabel(): string {
			if(this.authorData?.section === 'colleagues') {
				return this.$t('Teacher');
			}
			switch (this.authorData?.author.authorType) {
				case 'publisher': return this.$t('Publisher');
				case 'author': return this.$t('Author');
				case 'manufacturer': return this.$t('Toy manufacturer');
				case 'special': return this.$t('Other');
				default: return this.$t('Teacher');
			}
		},

		authorData(): AuthorData | null {
			if(this.section === 'colleagues') {
				return this.collectionsFromColleaguesStore.groupedByAuthor.find(g => g.author.id === this.authorId) || null;
			} else if(this.section === 'public') {
				return this.publicCollectionsStore.groupedByAuthor.find(g => g.author.id === this.authorId) || null;
			}
		},
	},

	methods: {
		urlsToHrefs(text: string): string {
			// Use the DOM to HTML-escape the raw text
			const div = document.createElement('div');
			div.textContent = text;
			const escapedText = div.innerHTML;
			// this will also result in & becoming &amp; in URL's, however that's not really an issue since browsers will interpret &amp; in hrefs as & anyway

			// Now safely replace URLs in the escaped string with anchor tags.
			return escapedText.replace(/https?:\/\/\S+|www\.\S+/gi, (url: string) => {
				// escape any trailing punctuation from the URL that shouldn't be part of the link (e.g., a period at the end of a sentence)
				const cleanUrl = url.replace(/[.,;:!?)'"]+$/, '');
				const href = cleanUrl.startsWith('http') ? cleanUrl : `https://${cleanUrl}`;
				return `<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>`;
			});
		}
	},
};
</script>
